import { Label } from "@/components/atoms/texts/label";
import { Separator } from "@/components/atoms/divider/separator";
import ListOfEditableOverview from "@/components/organisms/ListOfEditableOverview";
import { useState, useEffect, useMemo } from "react";
import Filter from "@/components/organisms/Filter";
import { useAuthContext } from '@/context/AuthContext';
import { Toaster } from "@/components/organisms/feedback/toaster";
import useStatement from "@/services/consultant-services/query/useStatement";
import useStatementYears from "@/services/consultant-services/query/useStatementYears";
import PrintErrorMessage from "@/components/organisms/feedback/PrintErrorMessage";
import StatementTable from "@/components/organisms/StatementTable";
import StatementOperationsChart from "@/components/organisms/charts/StatementOperationsChart";
import { Button } from "@/components/molecules/buttons/button";
import { Paid } from '@mui/icons-material';
import InvoicesTabUser from "./InvoicesTabUser";
import {useAppSelector} from '@/store/hooks.js';

export default function StatementTabUser() {
    const { role, hasPrivileges } = useAuthContext();
    const { allYears, errorYear } = useStatementYears();
    const user = useAppSelector((state) => state.auth.currentUser);
    
    // ✅ Calculer l'année par défaut : la plus récente disponible
    // Filter trie en DESC (2026, 2025, 2024) et on prend le premier élément
    // Pour conserver l'année sélectionnée lors du changement de consultant :
    // - Si selectedYear existe et est dans allYears → on garde cette année
    // - Sinon on prend la plus récente année disponible
    const defaultYear = useMemo(() => {
        if (!allYears || allYears.length === 0) return null;
        
        // Trier par ordre décroissant (comme Filter le fait)
        const sortedYearsDesc = [...allYears].sort((a, b) => b - a);
        
        // Retourner la plus RÉCENTE année = premier élément du tableau trié DESC
        return sortedYearsDesc[0];
    }, [allYears]);

    const [selectedYear, setSelectedYear] = useState(null);
    const [invoicesTab, setInvoiceTab] = useState(false);

    // ✅ Synchroniser selectedYear avec la logique de persistance
    // Si on change de consultant et que l'année actuelle existe toujours → on garde
    // Sinon on revient à l'année par défaut (la plus récente)
    useEffect(() => {
        if (!allYears || allYears.length === 0) return;
        
        // Si selectedYear existe déjà ET est toujours dans la nouvelle liste allYears
        if (selectedYear && allYears.includes(selectedYear)) {
            // On garde l'année actuelle (persistance entre consultants)
            return;
        }
        
        // Sinon on initialise avec l'année par défaut
        if (defaultYear) {
            setSelectedYear(defaultYear);
        }
    }, [allYears, defaultYear]);

    // ✅ Skip la requête tant que selectedYear n'est pas défini
    const { data: yearData, loading, error } = useStatement(
        selectedYear, 
        !selectedYear // skip si selectedYear est null/undefined
    );

    return (
        !invoicesTab ?
        <div className="h-[100%] w-[100%] flex flex-col gap-1 statementTab px-6 2xl:px-10 ">
            {errorYear && <PrintErrorMessage message={errorYear} />}
            {error && <PrintErrorMessage message={error} />}
            
            <div className=" flex flex-row w-full justify-between h-[47px] ">
                <div className="w-[100%] ml-[48%] flex flex-row justify-between float-right">
                    {allYears && allYears.length > 0 && (
                        <Filter
                            items={allYears}
                            informSelectedItem={setSelectedYear}
                            className={"float-right"}
                            defaultSelectedItem={selectedYear || defaultYear}
                            sortItemDESC={true} // ✅ Tri DESC (2026, 2025, 2024) par défaut
                        />
                    )}
                    <div className=" float-right">
                        {hasPrivileges("USER_INVOICE") &&
                            <Button
                                onClick={() => setInvoiceTab(true)}
                                leftIcon={<Paid />}
                                showLeftIcon
                                text="consultants.statement.overflow.seeBillsDetails"
                            />
                        }
                    </div>
                </div>
            </div>
            <div className=" flex  gap-1 ">
                <div className="flex flex-col  ">
                    <div className=" flex flex-col mb-firstDegree  ">
                        <Label
                            size="h2"
                            fontWeight="bold"
                            fontFamily=""
                            message="consultants.statement.overview.overview"
                            color="black"
                            className="font-700"
                        />
                    </div>
                    <ListOfEditableOverview
                        year={selectedYear}
                        yearData={yearData}
                        userActive={user}
                        className="w-[300px]"
                    />
                </div>
                <Separator
                    className={`${role === "admin"
                        ? "2xl:h-[118%] 2xl:mx-8 mx-4"
                        : "2xl:h-[100%] mx-8 "
                        } relative  h-[730px] w-[1px] `}
                    orientation="vertical"
                    color="secondaryBlue"
                    blur=""
                />
                <div className="grow  flex flex-col gap-thirdDegree">
                    <div className="flex flex-col items-start w-full gap-secondDegree">
                        <Label
                            size="h3"
                            fontWeight="bold"
                        >Récapitulatif</Label>
                        {loading && <span className="loader" />}
                        <StatementTable data={yearData} dataOwner />

                    </div>

                    {hasPrivileges("USER_STATEMENT") && selectedYear  && <div className="flex flex-col items-start w-full gap-thirdDegree">
                        <Label
                            size="h3"
                            fontWeight="bold"
                            message="consultants.statement.overflow.overflowFluctuation"
                        />
                         <StatementOperationsChart year={selectedYear} yearStatement={yearData} dataOwner={false} />

                    </div>}

                </div>
            </div>
            <Toaster />
        </div>
        :
        <InvoicesTabUser setInvoiceTab={setInvoiceTab} allYears={allYears} selectedYearParent={selectedYear} />
    );
}
